// src/pages/ClinicalNotes/templates/CreateTelehealthNote.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideoCallIcon from "@mui/icons-material/VideoCall";

import buildEhrNotePayload from "../../../utils/services/APP/ClinicalNotes/buildEhrNotePayload";
import { addEhrNote } from "../../../utils/services/AdvancedMD/ehrNotes";

const toUsDatetime = (date = new Date()) => {
  const d = new Date(date);
  const pad = (n) => (n < 10 ? `0${n}` : n);
  const h = d.getHours();
  const hour12 = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(
    hour12
  )}:${pad(d.getMinutes())}:00 ${ampm}`;
};

export default function CreateTelehealthNote({ patientId, patient, onCancel, onSaved }) {
  const [patientState, setPatientState] = useState({ patientId: "", profileId: "3" });

  const [meta, setMeta] = useState({
    templateName: "Telehealth Visit 2.0",
    comment: "Telehealth Visit 2.0",
    noteDatetime: toUsDatetime(),
    msgtime: toUsDatetime(),
    nocookie: "0",
  });

  const [location, setLocation] = useState({
    location: "Telehealth / Sante Plus Medical Center",
    location2: "",
    location1: "",
  });

  const [reason, setReason] = useState({
    reasonForAppointment: "Telehealth Visit",
  });

  const [vitals, setVitals] = useState({
    weight: "",
    height: "",
    bmi: "",
    temperature: "",
    pulse: "",
    o2SatRa: "",
    bpPrevSys: "",
    bpPrevDia: "",
    bp1Sys: "",
    bp1Dia: "",
    bpOption1: "",
    bpLocation1: "",
  });

  const [hpi, setHpi] = useState({
    general: "",
    tcm: "", // puedes usarlo como "Telehealth Summary" si quieres
  });

  const [meds, setMeds] = useState({
    allReviewedAndReconciled: false,
    text: "",
  });

  const [pmh, setPmh] = useState({
    noChangeReviewed: false,
    text: "",
  });

  const [ros, setRos] = useState({});
  const [exam, setExam] = useState({}); // Telehealth normalmente sin exam físico detallado
  const [phq2, setPhq2] = useState({ q1: "", q2: "", reviewedPhq9: "" });

  const [assessPlanFu, setAssessPlanFu] = useState({
    assessments: "",
    treatment: "",
    followUp: "",
  });

  const [disclaimer, setDisclaimer] = useState(
    "Telehealth visit performed per standard protocols. Patient consent obtained."
  );

  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    setPatientState({ patientId, profileId: "3" });
    const now = toUsDatetime();
    setMeta((m) => ({ ...m, noteDatetime: now, msgtime: now }));
  }, [patientId]);

  // BMI auto
  useEffect(() => {
    const h = parseFloat(vitals.height);
    const w = parseFloat(vitals.weight);
    if (!isNaN(h) && !isNaN(w) && h > 0) {
      const bmi = (w / (h * h)) * 703;
      setVitals((v) => ({ ...v, bmi: bmi.toFixed(1) }));
    }
  }, [vitals.height, vitals.weight]);

  const validate = () => {
    if (!patientState.patientId) return "Missing patient ID.";
    if (!reason.reasonForAppointment?.trim()) return "Reason for appointment is required.";
    const hasHpi = !!hpi.general?.trim();
    const hasAssess = !!assessPlanFu.assessments?.trim();
    if (!hasHpi && !hasAssess) return "Add at least HPI or an Assessment before saving.";
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      setSnack({ open: true, severity: "warning", message: err });
      return;
    }

    setSaving(true);
    const nowStr = toUsDatetime();

    // El disclaimer va dentro del Treatment para que quede guardado en el template
    const payload = buildEhrNotePayload({
      patient: patientState,
      meta: { ...meta, msgtime: nowStr, noteDatetime: nowStr },
      location,
      reason,
      vitals,
      hpi: {
        general: hpi.general,
        tcm: hpi.tcm,
      },
      meds,
      pmh,
      ros,
      exam,
      phq2,
      assessPlanFu: {
        ...assessPlanFu,
        treatment: [assessPlanFu.treatment, "", "— Telehealth Disclaimer —", disclaimer].join("\n"),
      },
    });

    try {
      const result = await addEhrNote(payload);
      const idTxt = result?.id ? ` (ID: ${result.id})` : "";
      setSnack({ open: true, severity: "success", message: `Clinical note created${idTxt}.` });
      onSaved?.(result);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Unknown error";
      setSnack({ open: true, severity: "error", message: `Error creating note: ${msg}` });
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, value, onChange, rows = 4 }) => (
    <TextField
      fullWidth
      multiline={rows > 1}
      rows={rows}
      label={label}
      value={value}
      onChange={onChange}
    />
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <VideoCallIcon sx={{ fontSize: 56 }} />
          <Typography variant="h4" fontWeight={900}>
            Telehealth Visit 2.0
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {patient?.fullName || patient?.name || patient?.patientName || "Patient"} • Patient ID: {patientId}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Location & Reason</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                label="Location"
                value={location.location}
                onChange={(e) => setLocation((x) => ({ ...x, location: e.target.value }))}
              />
              <TextField
                label="Reason for Appointment"
                value={reason.reasonForAppointment}
                onChange={(e) => setReason({ reasonForAppointment: e.target.value })}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">HPI</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Field
                label="History of Present Illness"
                value={hpi.general}
                onChange={(e) => setHpi((x) => ({ ...x, general: e.target.value }))}
                rows={10}
              />
              <Field
                label="Telehealth Summary / Notes (optional)"
                value={hpi.tcm}
                onChange={(e) => setHpi((x) => ({ ...x, tcm: e.target.value }))}
                rows={4}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Vitals (optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Weight (lb)"
                  value={vitals.weight}
                  onChange={(e) => setVitals((v) => ({ ...v, weight: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Height (in)"
                  value={vitals.height}
                  onChange={(e) => setVitals((v) => ({ ...v, height: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="BMI"
                  value={vitals.bmi}
                  onChange={(e) => setVitals((v) => ({ ...v, bmi: e.target.value }))}
                  fullWidth
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Pulse"
                  value={vitals.pulse}
                  onChange={(e) => setVitals((v) => ({ ...v, pulse: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Temperature"
                  value={vitals.temperature}
                  onChange={(e) => setVitals((v) => ({ ...v, temperature: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="O2 Sat RA"
                  value={vitals.o2SatRa}
                  onChange={(e) => setVitals((v) => ({ ...v, o2SatRa: e.target.value }))}
                  fullWidth
                />
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Meds & PMH (optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant={meds.allReviewedAndReconciled ? "contained" : "outlined"}
                  onClick={() =>
                    setMeds((m) => ({ ...m, allReviewedAndReconciled: !m.allReviewedAndReconciled }))
                  }
                >
                  Meds reviewed: {meds.allReviewedAndReconciled ? "YES" : "NO"}
                </Button>

                <Button
                  variant={pmh.noChangeReviewed ? "contained" : "outlined"}
                  onClick={() => setPmh((p) => ({ ...p, noChangeReviewed: !p.noChangeReviewed }))}
                >
                  PMH reviewed: {pmh.noChangeReviewed ? "YES" : "NO"}
                </Button>
              </Stack>

              <Field
                label="Current Medications"
                value={meds.text}
                onChange={(e) => setMeds((m) => ({ ...m, text: e.target.value }))}
                rows={4}
              />
              <Field
                label="PMH Details"
                value={pmh.text}
                onChange={(e) => setPmh((p) => ({ ...p, text: e.target.value }))}
                rows={4}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Assessments / Treatment / Follow-up</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Field
                label="Assessments"
                value={assessPlanFu.assessments}
                onChange={(e) => setAssessPlanFu((a) => ({ ...a, assessments: e.target.value }))}
                rows={5}
              />
              <Field
                label="Treatment"
                value={assessPlanFu.treatment}
                onChange={(e) => setAssessPlanFu((a) => ({ ...a, treatment: e.target.value }))}
                rows={5}
              />
              <Field
                label="Follow Up"
                value={assessPlanFu.followUp}
                onChange={(e) => setAssessPlanFu((a) => ({ ...a, followUp: e.target.value }))}
                rows={4}
              />
              <Field
                label="Telehealth Disclaimer"
                value={disclaimer}
                onChange={(e) => setDisclaimer(e.target.value)}
                rows={6}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? "Saving…" : "Create Note"}
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
